import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { flatten, keyBy } from 'lodash'
import { notEmpty, useForm } from '@xvii/useform'
import config from 'cityads-v3/config'
import { useEntities } from 'cityads-v3/hooks/entities'
import { useFormState } from 'cityads-v3/hooks/form'
import { BannerSizeList } from 'cityads-v3/types/banner'
import { Offer, OffersList } from 'cityads-v3/types/offers'
import { SUBSCRIPTION_STATUS_ID_AVAILABLE, TOOL_ID_BANNER_ROTATOR } from 'cityads-v3/reducers/offers'
import { useDebounceAction } from 'cityads-v3/utils/useUtils'
import { CategoryItemList } from 'cityads-v3/types/categories'
import { PromoList } from 'cityads-v3/types/promo'
import { createBannerRotator, editBannerRotator } from 'cityads-v3/reducers/bannerRotator'
import { BannerRotator, BannerRotatorList } from 'cityads-v3/types/bannerRotator'
import { PromoType } from 'cityads-v3/reducers/promo'
import { RootState } from 'cityads-v3/types/reducer'

export function useCreatePage(offerId?: Offer['id'], onSuccess?: (bannerRotator: BannerRotator) => void) {
  const dispatch = useDispatch()
  const { t } = useTranslation(['main'])
  const formState = useFormState()
  const currentBannerRotator = useGetCurrentBannerRotator()
  const preloadDictionaries = usePreloadDictionaries()

  const handleSubmit = useCallback(async (data: BannerRotator) => {
    try {
      formState.setIsSending(true)
      const result = currentBannerRotator?.id ?
        await dispatch(editBannerRotator(currentBannerRotator.id, data))
        :
        await dispatch(createBannerRotator(data))
      formState.setSucceeded(result)
      onSuccess?.(result);
    } catch (err) {
      formState.setFailed(err?.data?.form_errors)
    }

  }, [dispatch, currentBannerRotator?.id, onSuccess])

  const form = useForm({
    onSubmit: handleSubmit,
    fields: {
      name: {
        validators: [
          notEmpty({ errorText: t('forms.errorFieldNoEmpty') })
        ],
        valueType: String(),
      },
      banner_type: { defaultValue: 'image' },
      banner_sizes: { defaultValue: [], valueType: [Number()] },
      subaccounts: { defaultValue: [''], valueType: [String()] },
      add_new_offer: { defaultValue: true, valueType: Boolean() },
      enable_optimization: { defaultValue: false, valueType: Boolean() },

      offer_ids: { defaultValue: offerId ? [offerId] : [], valueType: [Number()] },
      exclude_offer_ids: { defaultValue: [], valueType: [Number()] },

      category_ids: { defaultValue: [], valueType: [Number()] },
      exclude_category_ids: { defaultValue: [], valueType: [Number()] },

      banner_ids: { defaultValue: [], valueType: [Number()] },
      exclude_banner_ids: { defaultValue: [], valueType: [Number()] },
    }
  })

  const handleResetForm = useCallback(() => {
    form.reset()
    formState.setIsSent(false)
  }, [form, formState])

  const entityList = useEntityLists(form)

  useEffect(() => {
    if (currentBannerRotator) {
      form.setFormData(currentBannerRotator, true)
      preloadDictionaries({
        offer_ids: [
          ...currentBannerRotator.offer_ids,
          ...currentBannerRotator.exclude_offer_ids
        ],
        banner_ids: [
          ...currentBannerRotator.banner_ids,
          ...currentBannerRotator.exclude_banner_ids
        ]
      })
    }
  }, [currentBannerRotator, form])

  return {
    form,
    formState,
    entityList,
    handleResetForm
  }
}

/**
 * Справочники для полей формы
 */
function useEntityLists(form: ReturnType<typeof useForm>) {
  const bannerSizeList = useEntities<BannerSizeList>('bannerSize', null, true);

  const offerList = useEntities<OffersList>('offersList')
  const [offersListAllLoaded] = useEntities<OffersList[]>('offersListAllLoaded')
  const [, , , loadOfferList] = offerList;
  const oldOffersById = useSelector((state: RootState) => Object.values(state.offers.offersById))

  const offerListFull = useMemo(() => ([
    ...flatten(offersListAllLoaded?.map(item => item.offer)),
    ...oldOffersById
  ]), [offersListAllLoaded, oldOffersById])

  const categoryList = useEntities<CategoryItemList>('categories', null, true);
  const promoList = useEntities<PromoList>('promo');
  const [promoListAllLoaded] = useEntities<PromoList[]>('promoListAllLoaded')
  const [, , , loadPromoList] = promoList;
  const promoListFull = useMemo(() => flatten(
    promoListAllLoaded?.map(item => item.promo)
  ), [promoListAllLoaded])

  const filterForm = useForm({
    fields: {
      offerName: { valueType: String() },
      bannerName: { valueType: String() },
      bannerSize: { valueType: String() },
      categoryName: { valueType: String() },
    }
  })

  const loadOfferListAction = useDebounceAction(useCallback(() => {
    loadOfferList({
      filter: {
        name: filterForm.fields.offerName.value,
        tool_id: [TOOL_ID_BANNER_ROTATOR],
        subscription_status: [SUBSCRIPTION_STATUS_ID_AVAILABLE],
        banner_size_ids: form.fields.banner_sizes.value,
        sort: 'name',
        sort_asc: true
      }
    })
  }, [
    form.fields.banner_sizes.value,
    filterForm.fields.offerName.value,
    loadOfferList
  ]), config.KEYBOARD_TIMEOUT)

  const loadPromoListAction = useDebounceAction(useCallback(() => {
    loadPromoList({
      name: filterForm.fields.bannerName.value,
      types: [PromoType.image],
      sort: 'name',
      sort_asc: true
    })
  }, [
    filterForm.fields.bannerName.value,
    loadPromoList
  ]), config.KEYBOARD_TIMEOUT)

  useEffect(loadOfferListAction, [loadOfferListAction])
  useEffect(loadPromoListAction, [loadPromoListAction])

  return {
    filterForm,
    bannerSizeList,
    offerList,
    offerListFull,
    loadOfferList,
    categoryList,
    promoList,
    promoListFull,
  }
}

function useGetCurrentBannerRotator() {
  const params = useParams<{ bannerRotatorId: string }>();
  const id = +params.bannerRotatorId;
  const [bannerRotators, isLoading, , load] = useEntities<BannerRotatorList>('bannerRotatorTool');
  const bannerRotatorsById = useMemo(() => keyBy(bannerRotators?.bannerRotator, 'id'), [bannerRotators])

  const currentBannerRotator = bannerRotatorsById[id]

  useEffect(() => {
    if (id && !currentBannerRotator && !isLoading) {
      load({
        ids: [id]
      })
    }
  }, [currentBannerRotator, isLoading, load, id])

  return currentBannerRotator;
}


/**
 * Предзагрузка списков для сохраненного инструмента
 */
type PreloadDictionariesProps = {
  offer_ids: number[]
  banner_ids: number[]
}

function usePreloadDictionaries() {
  const [, , , loadOfferList] = useEntities<OffersList>('offersListAllLoaded')
  const [, , , loadPromoList] = useEntities<OffersList>('promoListAllLoaded')

  const load = useCallback((data: PreloadDictionariesProps) => {
    loadOfferList({
      filter: {
        id: data.offer_ids
      }
    })

    loadPromoList({
      ids: data.banner_ids
    })
  }, [
    loadOfferList,
    loadPromoList
  ])

  return load;
}
